import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Menu, Building2, Briefcase, FileText, Bookmark, MessageSquare, LogOut, User, CreditCard } from 'lucide-react';
import SearchBar from '../../features/search/components/SearchBar';
import NotificationBell from '../../features/notifications/components/NotificationBell';
import PostOpportunityModal from '../../features/opportunities/components/PostOpportunityModal';
import ShareCaseWizard from '../../features/cases/components/ShareCaseWizard';
import { useAuth } from '../../contexts/AuthContext';
import CoFounderBadge from '../ui/CoFounderBadge';
import { isCoFounder } from '../../utils/cofounderBadge';

const Header = ({ onToggleSidebar }) => {
  const [isBagOpen, setIsBagOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const bagRef = useRef(null);
  const profileRef = useRef(null);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const userInitial = user?.name ? user.name.charAt(0).toUpperCase() : 'U';

  // Sync URL search param ?action=share-case with modal state
  useEffect(() => {
    if (searchParams.get('action') === 'share-case') {
      setShowShareModal(true);
    }
  }, [searchParams]);

  const handleOpenShareModal = () => {
    setShowShareModal(true);
    const params = new URLSearchParams(searchParams);
    params.set('action', 'share-case');
    setSearchParams(params, { replace: true });
  };

  const handleCloseShareModal = () => {
    setShowShareModal(false);
    if (searchParams.get('action') === 'share-case') {
      const params = new URLSearchParams(searchParams);
      params.delete('action');
      setSearchParams(params, { replace: true });
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (bagRef.current && !bagRef.current.contains(event.target)) setIsBagOpen(false);
      if (profileRef.current && !profileRef.current.contains(event.target)) setIsProfileOpen(false);

    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
  };

  const bagMenuItems = [
    ...(user?.role !== 'student' ? [{ 
      icon: <FileText size={18} color="#4A5565" />, 
      label: 'My Cases',
      onClick: () => { setIsBagOpen(false); navigate('/cases/mine'); }
    }] : []),
    { 
      icon: <Bookmark size={18} color="#4A5565" />, 
      label: 'Saved Cases',
      onClick: () => { setIsBagOpen(false); navigate('/cases/saved'); }
    },
    { 
      icon: <MessageSquare size={18} color="#4A5565" />, 
      label: 'My Inputs',
      onClick: () => { setIsBagOpen(false); navigate('/cases/my-inputs'); }
    },
    {
      icon: <Briefcase size={18} color="#4A5565" />,
      label: 'Opportunities',
      onClick: () => { setIsBagOpen(false); navigate('/opportunities'); },
    },
    // Vets: link to My Job Postings
    ...(user?.role === 'veterinarian' ? [{
      icon: <Briefcase size={18} color="#6b46c1" />,
      label: 'My Job Postings',
      onClick: () => { setIsBagOpen(false); navigate('/my-postings'); },
    }] : []),
    // All users: link to My Applications
    {
      icon: <Briefcase size={18} color="#6b46c1" />,
      label: 'My Applications',
      onClick: () => { setIsBagOpen(false); navigate('/my-applications'); },
      highlight: true,
    },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
      <div className="flex items-center justify-between px-3 sm:px-5 h-14">
        {/* Left: Hamburger + Logo */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            id="menu-toggle"
            onClick={onToggleSidebar}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors duration-200 lg:hidden"
          >
            <Menu size={20} className="text-text-black" />
          </button>
          <span className="text-[25px] font-bold text-text-black tracking-tight">Vet Collab</span>
        </div>

        {/* Center: Search bar — absolutely centered */}
        <div className="hidden sm:flex flex-1 justify-center mx-4">
          <SearchBar />
        </div>

        {/* Right Actions */}
        <div className="flex items-center mr-6 gap-1 sm:gap-2 flex-shrink-0">
          {user?.role !== 'student' && (
            <button
              id="share-case-btn"
              onClick={handleOpenShareModal}
              className="hidden md:inline-flex px-4 py-1.5 text-sm font-medium text-accent-indigo border border-accent-indigo 
                         rounded-lg hover:bg-accent-indigo hover:text-white transition-all duration-200"
            >
              Share a Case
            </button>
          )}
          {/* Hire Vet Button */}
          {user?.role !== 'student' && (
            <button
              id="hire-vet-btn"
              onClick={() => setShowPostModal(true)}
              className="hidden lg:inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-text-black 
                         border border-gray-200 rounded-lg hover:bg-gray-50 transition-all duration-200"
            >
              <Building2 size={15} />
              <span>Hire Vet</span>
            </button>
          )}

          {/* Bag Icon with Dropdown */}
          <div className="relative" ref={bagRef}>
            <button
              id="bag-btn"
              onClick={() => setIsBagOpen(!isBagOpen)}
              className={`p-2 rounded-lg transition-colors duration-200 ${isBagOpen ? 'bg-gray-100' : 'hover:bg-gray-50'
                }`}
            >
              <Briefcase size={20} color="#4A5565" />
            </button>

            {isBagOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-[60] animate-fadeIn">
                {bagMenuItems.map((item, index) => (
                  <button
                    key={index}
                    onClick={item.onClick}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors duration-150 text-left ${
                      item.highlight ? 'text-[#6b46c1] font-semibold' : 'text-text-black'
                    }`}
                  >
                    <span className="text-gray-500">{item.icon}</span>
                    <span className="font-medium">{item.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <NotificationBell />

          {/* Profile Avatar with Dropdown */}
          <div className="relative" ref={profileRef}>
            <button
              id="profile-btn"
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="w-8 h-8 rounded-full bg-gradient-to-br from-accent-indigo to-accent-blue 
                         flex items-center justify-center text-white text-xs font-bold
                         ring-2 ring-white shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden"
            >
              {user?.avatarUrl ? (
                <img src={user.avatarUrl} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                userInitial
              )}
            </button>

            {isProfileOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-[60] animate-fadeIn">
                <div className="px-4 py-2 border-b border-gray-50 mb-1">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {isCoFounder(user)
                      ? user?.name
                      : (user?.role === 'veterinarian' ? `Dr. ${user?.name}` : user?.name)}
                  </p>
                  {user?.jobRole && (
                    <p className="text-[11px] font-medium text-indigo-600 truncate">{user.jobRole}</p>
                  )}
                  <p className="text-xs text-gray-400 font-medium truncate mt-0.5">{user?.email}</p>
                </div>

                <button
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-text-black hover:bg-gray-50 transition-colors duration-150 text-left"
                  onClick={() => {
                    setIsProfileOpen(false);
                    navigate('/profile');
                  }}
                >
                  <User size={16} className="text-gray-500" />
                  <span>My Profile</span>
                </button>
                
                {user?.role === 'student' && (
                  <button
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-[#6b46c1] hover:bg-[#6b46c1]/5 transition-colors duration-150 text-left"
                    onClick={() => {
                      setIsProfileOpen(false);
                      navigate('/subscription');
                    }}
                  >
                    <CreditCard size={16} />
                    <span>Subscription Plan</span>
                  </button>
                )}

                <button
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150 text-left mt-1"
                  onClick={handleLogout}
                >
                  <LogOut size={16} />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="sm:hidden px-3 pb-2">
        <SearchBar />
      </div>

      <PostOpportunityModal 
        isOpen={showPostModal}
        onClose={() => setShowPostModal(false)}
      />

      <ShareCaseWizard
        isOpen={showShareModal}
        onClose={handleCloseShareModal}
      />
    </header>
  );
};

export default Header;